import { ITypedEvent } from "../../TypedEvent";
import { IEvent, Event } from "../../Event";
import { ControllerConfigurationErrorCode, IControllerConfigurationService } from "./IControllerConfigurationService";
import { TypedEvent } from '../../TypedEvent';
import { IExalusConnectionService } from '../IExalusConnectionService';
import { Api } from "../../Api";
import { ExalusConnectionService } from '../ExalusConnectionService';
import { ISessionService } from "../Session/ISessionService";
import { SessionService } from '../Session/SessionService';
import { DataFrame, Method, Status } from '../../DataFrame';
import { ILocalStorageService } from '../ILocalStorageService';
import { LocalStorageService } from "../LocalStorageService";
import { WebApiCacheService } from "../WebApi/WebApiCacheService";
import { IWebApiCacheService } from "../WebApi/IWebApiCacheService";
import { Task } from '../../Helpers';
import { ControllerExtensionsService } from './ControllerExtensionsService';
import { AccessLevel } from "../Users/IUser";
import { ResponseResult } from "../FieldChangeResult";
import { DependencyContainer } from "../../DependencyContainer";

export class ControllerConfigurationService implements IControllerConfigurationService {
    private _connection: IExalusConnectionService | null = null;
    private _session: ISessionService | null = null;
    private _localStorage: ILocalStorageService | null = null;
    public static readonly ServiceName: string = "ControllerConfigurationService";
    private _onEnteredConfigurationEvent: IEvent = new Event();
    private _onExitedConfigurationEvent: IEvent = new Event();
    private _onConfigurationTimeCheckedEvent: ITypedEvent<Date> = new TypedEvent<Date>();
    private _configurationTime: Date = new Date();
    private _appEnteredConfigurationMode: boolean = false;
    private _didConfigurationChange: boolean = true;
    public static ConfigurationTimeRetrieved: Date | null = null;
    private _didSynchronization: boolean = false;
    private _onSynchronized: Event = new Event();
    private _onConfigurationChangedEvent: IEvent = new Event();

    OnConfigurationChangedEvent(): IEvent {
        return this._onConfigurationChangedEvent;
    }

    GetServiceName(): string {
        return ControllerConfigurationService.ServiceName;
    }

    constructor() {
        this._connection = Api.Get<IExalusConnectionService>(ExalusConnectionService.ServiceName);
        this._session = Api.Get<ISessionService>(SessionService.ServiceName);
        this._localStorage = Api.Get<ILocalStorageService>(LocalStorageService.ServiceName);
        let removeSubCallback = this._connection.SubscribeTo<any>("/info/configuration/mode/entry", (frame) => {
            Api.Get<IWebApiCacheService>(WebApiCacheService.ServiceName).ClearCache();

            if (!this._appEnteredConfigurationMode)
                (this._onEnteredConfigurationEvent as Event).Invoke();
        });

        let removeSubCallback2 = this._connection.SubscribeTo<any>("/info/configuration/mode/exit", (frame) => {
            Api.Get<IWebApiCacheService>(WebApiCacheService.ServiceName).ClearCache();

            if (!this._appEnteredConfigurationMode)
                (this._onExitedConfigurationEvent as Event).Invoke();

            (this.OnConfigurationChangedEvent() as Event)?.Invoke();
        });
    }


    public CheckIfControllerSoftwareVersionIsEnough(requiredContainerMajor: number, requiredContainerMinor: number): boolean {
        const versionString = this._session?.User?.SoftwareVersion ?? null;

        if (!versionString || !versionString.includes('.'))
            return false;

        const segments = versionString.split('.');
        if (segments.length !== 2)
            return false;

        const numbers = segments.map(s => parseInt(s, 10));
        if (numbers.some(n => Number.isNaN(n) || n < 0))
            return false;

        const [containerMajor, containerMinor] = numbers as [number, number];
        return (containerMajor > requiredContainerMajor) || (containerMajor === requiredContainerMajor && containerMinor >= requiredContainerMinor);
    }

    async ImportControllerConfigurationAsync(file: File): Promise<Status> {
        let conf = await file.text();
        let chunks = this.SplitStringBySize(conf);

        let req = new ImportControllerConfigurationRequest("");
        req.Status = Status.MultiDataResponseStart;
        let result = await this._connection?.SendAndWaitForResponseAsync<string>(req, 6000, false);

        if (result === undefined || result?.Status != Status.OK)
            return Status.Error;

        for (const chunk of chunks) {
            let req = new ImportControllerConfigurationRequest(chunk);
            req.Status = Status.MultiDataResponse;
            let result = await this._connection?.SendAndWaitForResponseAsync<string>(req, 6000, false);

            if (result === undefined || result?.Status != Status.OK)
                return Status.Error;
        }
        req.Status = Status.MultiDataResponseStop;
        let result2 = await this._connection?.SendAndWaitForResponseAsync<string>(req, 120_000, false);

        if (result2 === undefined || result2?.Status != Status.OK)
            return Status.Error;

        return Status.OK;
    }

    SplitStringBySize(input: string, maxSizeInBytes: number = 100000): string[] {
        // Calculate the approximate maximum number of characters
        const maxChars = Math.floor(maxSizeInBytes / 2);

        const result: string[] = [];
        let startIndex = 0;

        while (startIndex < input.length) {
            // Get the chunk of the string
            let chunk = input.slice(startIndex, startIndex + maxChars);

            // Check the actual byte size of the chunk and adjust if necessary
            while (new Blob([chunk]).size > maxSizeInBytes) {
                // Reduce the size of the chunk until it fits within the byte limit
                chunk = chunk.slice(0, -1);
            }

            // Add the chunk to the result array
            result.push(chunk);

            // Move to the next chunk
            startIndex += chunk.length;
        }

        return result;
    }

    async GetControllerSystemTimeAsync(): Promise<Date | Status> {
        try {
            let result = await this._connection?.SendAndWaitForResponseAsync<string>(new GetControllerTimeRequest(), 8000, false);

            if (result == null || result.Status == null)
                return Status.Error;

            if (result.Status != Status.OK)
                return result.Status;

            if (result.Data == null)
                return Status.ResourceIsNotAvailable;

            return new Date(result.Data);
        } catch (error) {
            DependencyContainer.Log?.Error(ControllerConfigurationService.ServiceName, `Cannot get current controller time! ${error}`);
            return Status.FatalError;
        }
    }

    async SetControllerSystemTimeAsync(date: Date): Promise<Status> {
        try {
            let result = await this._connection?.SendAndWaitForResponseAsync<string>(new SetControllerTimeRequest(date.toISOString()), 8000, false);

            if (result == null || result.Status == null)
                return Status.Error;

            return result.Status;
        } catch (error) {
            DependencyContainer.Log?.Error(ControllerConfigurationService.ServiceName, `Cannot set current controller time! ${error}`);
            return Status.FatalError;
        }
    }

    async StartNtpTimeSynchronizationAsync(): Promise<Status> {
        try {
            let result = await this._connection?.SendAndWaitForResponseAsync<string>(new SetNtpControllerTimeRequest(), 8000, false);

            if (result == null || result.Status == null)
                return Status.Error;

            return result.Status;
        } catch (error) {
            DependencyContainer.Log?.Error(ControllerConfigurationService.ServiceName, `Cannot set current controller time! ${error}`);
            return Status.FatalError;
        }
    }

    async FactoryResetAsync(func: (progress: number) => void): Promise<Status> {
        try {
            Api.Get<IWebApiCacheService>(WebApiCacheService.ServiceName).ClearCache();
            let result = (await this._connection?.SendAndWaitForResponseAsync(new DoFactoryResetRequest(), 2000, false))?.Status as Status;

            if (result == Status.OK) {
                let Seconds = 300; //in seconds 
                let currentProgress = 0;
                let progressPerStep = 100 / Seconds;
                let id = setInterval(() => {
                    currentProgress += progressPerStep;
                    func(parseFloat(currentProgress.toPrecision(1)));
                }, 1000);
                await Task.Delay(Seconds * 1000);
                Api.Get<IWebApiCacheService>(WebApiCacheService.ServiceName).ClearCache();
                clearInterval(id);
                return Status.OK;
            }
            else
                return result;
        }
        catch (e) {
            return Status.FatalError;
        }
    }

    async RollbackLastUpdateAsync(func: (progress: number) => void): Promise<Status> {
        try {
            Api.Get<IWebApiCacheService>(WebApiCacheService.ServiceName).ClearCache();
            let result = (await this._connection?.SendAndWaitForResponseAsync(new RestorePreviousVersionRequest(), 2000, false))?.Status as Status;

            if (result == Status.OK) {
                let Seconds = 420; //in seconds 
                let currentProgress = 0;
                let progressPerStep = 100 / Seconds;
                let id = setInterval(() => {
                    currentProgress += progressPerStep;
                    func(parseFloat(currentProgress.toPrecision(1)));
                }, 1000);
                await Task.Delay(Seconds * 1000);
                Api.Get<IWebApiCacheService>(WebApiCacheService.ServiceName).ClearCache();
                clearInterval(id);
                return Status.OK;
            }
            else
                return result;
        }
        catch (e) {
            return Status.FatalError;
        }
    }

    async RestartControllerAsync(func: (progress: number) => void): Promise<Status> {

        try {
            Api.Get<IWebApiCacheService>(WebApiCacheService.ServiceName).ClearCache();
            let result = (await this._connection?.SendAndWaitForResponseAsync(new RestartControllerRequest(), 2000, false))?.Status as Status;

            if (result == Status.OK) {
                let Seconds = 240; //in seconds 
                let currentProgress = 0;
                let progressPerStep = 100 / Seconds;
                let id = setInterval(() => {
                    currentProgress += progressPerStep;
                    func(parseFloat(currentProgress.toPrecision(1)));
                }, 1000);
                await Task.Delay(Seconds * 1000);
                Api.Get<IWebApiCacheService>(WebApiCacheService.ServiceName).ClearCache();
                clearInterval(id);
                return Status.OK;
            }
            else
                return result;
        }
        catch (e) {
            return Status.FatalError;
        }
    }

    async DidCofigurationChangeAsync(): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            if (this._didSynchronization)
                resolve(this._didConfigurationChange);
            else
                this._onSynchronized?.Subscribe(() => {
                    resolve(this._didConfigurationChange);
                });
        });
    }

    private GetLastKnownConfigurationChangeTime(): Date {
        const result = this._localStorage?.Read<string>(this.GetServiceName(), "last_configuration_time");

        if (result != null)
            return new Date(result);
        else
            return new Date();
    }

    private SaveLastConfigurationChangeTime(time: Date): void {
        this._localStorage?.Save(this.GetServiceName(), "last_configuration_time", time.toString());
    }

    async EnterConfigurationModeAsync(): Promise<void> {
        this._appEnteredConfigurationMode = true;
        Api.Get<IWebApiCacheService>(WebApiCacheService.ServiceName).ClearCache();
        await this._connection?.SendAndWaitForResponseAsync(new EntryConfigurationModeRequest(), 20000, false);
    }

    async ExitConfigurationModeAsync(): Promise<void> {
        this._appEnteredConfigurationMode = true;
        Api.Get<IWebApiCacheService>(WebApiCacheService.ServiceName).ClearCache();
        await this._connection?.SendAndWaitForResponseAsync(new ExitConfigurationModeRequest(), 20000, false);
        this._didConfigurationChange = true;
        Api.Get<IWebApiCacheService>(WebApiCacheService.ServiceName).ClearCache();
        this._appEnteredConfigurationMode = false;
    }

    OnEnteredConfigurationEvent(): IEvent {
        return this._onEnteredConfigurationEvent;
    }

    OnExitedConfigurationEvent(): IEvent {
        this._didConfigurationChange = true;
        return this._onExitedConfigurationEvent;
    }

    OnConfigurationTimeCheckedEvent(): ITypedEvent<Date> {
        return this._onConfigurationTimeCheckedEvent;
    }

    async GetLastConfigurationChangeTimeAsync(): Promise<Date> {
        const result = await this._connection?.SendAndWaitForResponseAsync<string>(new LastChangeTimeRequest(), 20000, false);

        if (result == null || result?.Status != Status.OK)
            throw new Error("Failed to get last configuration change time");

        this._configurationTime = new Date(result.Data as string);
        return this._configurationTime;
    }

    async CheckIfConfigurationHasChangedAsync(): Promise<boolean> {
        const result = await this.GetLastConfigurationChangeTimeAsync();
        DependencyContainer.Log?.Info(ControllerConfigurationService.ServiceName, `Checking if configuration time has changed. Current: ${result.toString()}, Last known: ${this.GetLastKnownConfigurationChangeTime().toString()}`);
        if (this.GetLastKnownConfigurationChangeTime().toString() != result.toString()) {
            this.SaveLastConfigurationChangeTime(result);
            Api.Get<IWebApiCacheService>(WebApiCacheService.ServiceName).ClearCache();
            this._didSynchronization = true;
            this._didConfigurationChange = true;
            (this.OnConfigurationChangedEvent() as Event)?.Invoke();
            (this._onConfigurationTimeCheckedEvent as TypedEvent<Date>).Invoke(result);
            this._onSynchronized?.Invoke();
            return true;
        }
        else {
            this._didSynchronization = true;
            this._didConfigurationChange = false;
            this._onSynchronized?.Invoke();
            return false;
        }
    }


    async CheckIfConfigurationTimeHasChangedAsync(currentConfigurationTime: Date): Promise<boolean> {
        DependencyContainer.Log?.Info(ControllerConfigurationService.ServiceName, `Checking if configuration time has changed. Current: ${currentConfigurationTime.toString()}, Last known: ${this.GetLastKnownConfigurationChangeTime().toString()}`);
        if (this.GetLastKnownConfigurationChangeTime().toString() != currentConfigurationTime.toString()) {
            this.SaveLastConfigurationChangeTime(currentConfigurationTime);
            Api.Get<IWebApiCacheService>(WebApiCacheService.ServiceName).ClearCache();
            this._didSynchronization = true;
            this._didConfigurationChange = true;
            (this.OnConfigurationChangedEvent() as Event)?.Invoke();
            (this._onConfigurationTimeCheckedEvent as TypedEvent<Date>).Invoke(currentConfigurationTime);
            this._onSynchronized?.Invoke();
            return true;
        }
        else {
            this._didSynchronization = true;
            this._didConfigurationChange = false;
            this._onSynchronized?.Invoke();
            return false;
        }
    }

    async CanExportOrImportControllerConfigurationAsync(): Promise<boolean> {
        try {
            let ext = await Api.Get<ControllerExtensionsService>(ControllerExtensionsService.ServiceName).GetExtensionsInfoAsync();
            let user = Api.Get<ISessionService>(SessionService.ServiceName).User;

            if (user == null || user.AccessLevel == null)
                return false;

            return user.AccessLevel >= AccessLevel.Admin && ext.any(e => e.ExtensionGuid == "f4f0f066-7c43-46c6-a0bd-aedf93903dc0");
        }
        catch (e) {
            return false;
        }
    }

    async ExportControllerConfigurationAsync(): Promise<HTMLAnchorElement | ResponseResult<ControllerConfigurationErrorCode>> {
        let data: string[] = [];
        let err: ResponseResult<ControllerConfigurationErrorCode> | null = null;
        await this._connection?.SendAndHandleResponseAsync<string>(new ExportControllerConfigurationRequest(), 30_000, (result) => {
            switch (result.Status) {
                case Status.MultiDataResponseStart:

                    break;

                case Status.MultiDataResponse:
                    if (result.Data != null && result.Data !== undefined)
                        data.push(result.Data);
                    break;

                case Status.MultiDataResponseStop:

                    break;

                case Status.Error:
                case Status.FatalError:
                    err = new ResponseResult<ControllerConfigurationErrorCode>(ControllerConfigurationErrorCode.CannotExportConfiguration, "Cannot export configuration! Controller returned error!");
            }
        }, true);

        let prettyPrint = JSON.stringify(JSON.parse(data.join('')), null, 2);
        console.log(prettyPrint);

        if (err != null)
            return err;

        return Api.ExportStringAsFileToDownload(this.GetCurrentTimestampedFilename(), prettyPrint);
    }

    private GetCurrentTimestampedFilename(): string {
        const now = new Date();
        const hours = now.getHours().toString().padStart(2, '0');
        const minutes = now.getMinutes().toString().padStart(2, '0');
        const day = now.getDate().toString().padStart(2, '0');
        const month = (now.getMonth() + 1).toString().padStart(2, '0'); // Month is zero-based
        const year = now.getFullYear().toString();
        return `${this._connection?.GetAuthorizationInfo()?.SerialNumber}_${hours}-${minutes}-${day}-${month}-${year}-config.cexp`;
    }
}

class ImportControllerConfigurationRequest extends DataFrame<string> {
    constructor(data: string) {
        super();
        this.Resource = "/configuration/backup/import";
        this.Method = Method.Put;
        this.Data = data;
    }
}

class ExportControllerConfigurationRequest extends DataFrame<any> {
    constructor() {
        super();
        this.Resource = "/configuration/backup/export";
        this.Method = Method.Get;
    }
}

class RestartControllerRequest extends DataFrame<any> {
    constructor() {
        super();
        this.Resource = "/controller/restart";
        this.Method = Method.Post;
    }
}

class DoFactoryResetRequest extends DataFrame<any> {
    constructor() {
        super();
        this.Resource = "/controller/factory_reset";
        this.Method = Method.Post;
    }
}

class RestorePreviousVersionRequest extends DataFrame<any> {
    constructor() {
        super();
        this.Resource = "/controller/backups/restore/previous";
        this.Method = Method.Post;
    }
}

class EntryConfigurationModeRequest extends DataFrame<any> {
    constructor() {
        super();
        this.Resource = "/system/configuration/mode/entry";
        this.Method = Method.Put;
    }
}

class ExitConfigurationModeRequest extends DataFrame<any> {
    constructor() {
        super();
        this.Resource = "/system/configuration/mode/exit";
        this.Method = Method.Put;
    }
}

class LastChangeTimeRequest extends DataFrame<any> {
    constructor() {
        super();
        this.Resource = "/system/configuration/change/time";
        this.Method = Method.Get;
    }
}

class GetControllerTimeRequest extends DataFrame<any> {
    constructor() {
        super();
        this.Resource = "/system/time";
        this.Method = Method.Get;
    }
}

class SetControllerTimeRequest extends DataFrame<string> {
    constructor(date: string) {
        super();
        this.Resource = "/system/time";
        this.Method = Method.Put;
        this.Data = date;
    }
}

class SetNtpControllerTimeRequest extends DataFrame<any> {
    constructor() {
        super();
        this.Resource = "/system/time/ntp/update";
        this.Method = Method.Put;
    }
}